import { useEffect, useRef } from "react"
import type { ReactNode } from "react"
import { ArrowBack } from "@mui/icons-material"
import { Box } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { KellyPanePage } from "../kelly-pane-page"
import { SkillNavButton } from "./skill-nav-button"

// The scroll container's padding. Exported because a sticky child has to cancel it on both axes: `top` is
// resolved against this element's PADDING box, so a sticky bar at top:0 pins one padding-step below the
// header no matter what its margins say.
export const SKILLS_BODY_PADDING = 2
const BODY_SX = { flex: 1, minHeight: 0, overflowY: "auto", p: SKILLS_BODY_PADDING } satisfies SxProps<Theme>

interface KellySkillsPageProps {
  children?: ReactNode
  /** Return to the list. Absent on the list itself, which has nothing to go back to. */
  onBack?: () => void
  /** Changes when the body's content does, so the new content starts at the top. */
  scrollResetKey: string
}

export function KellySkillsPage({ children, onBack, scrollResetKey }: KellySkillsPageProps) {
  const backRef = useRef<HTMLButtonElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  // The body is the one scroll container, and swapping the list for a detail does not touch it -- so a
  // detail opened from the bottom of a long grid starts scrolled past its own title and back control.
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0 }, [scrollResetKey])

  const backButton = onBack && (
    <SkillNavButton
      ref={backRef}
      startIcon={<ArrowBack fontSize="small" />}
      onClick={onBack}
      data-testid="kelly-skills-back-to-list"
    >
      All skills
    </SkillNavButton>
  )

  return (
    <KellyPanePage title="Skills" testId="kelly-skills-page" headerStart={backButton} focusTargetRef={backRef} refocusKey={scrollResetKey}>
      <Box ref={bodyRef} sx={BODY_SX} data-testid="kelly-skills-page-body">{children}</Box>
    </KellyPanePage>
  )
}
